import axiosApi from './axiosApi';
import {tokenStorage} from '../app/tokenStorage';
import {encrypt} from './securityApi';
import {
    ORGANISATIONS_LIST,
    ORGANISATION_REPORT,
    ORGANISATION_MEMBERS,
    ORGANISATION_TAGS,
    ORGANISATION_ADDFORM,
    ORGANISATION_SUBSCRIPTION,
    ORGANISATION_UPDATE_DEFAULT,
    ORGANISATION_ADD_TAG,
    ORGANISATION_SEARCH_BY_DOMAIN,
    ORGANISATION_TIMESHEETS_EXPORT,
    ORGANISATION_PRESENCE_EXPORT,
} from './routes/organisation_routes';
import dayjs from 'dayjs';
import {localDecrypt} from '../utils/localDecrypt';

// Fast local decryption - no remote calls
const fastDecrypt = (data) => {
    if (!data) return null;
    if (typeof data === 'object') return data;
    if (typeof data === 'string') {
        try { return JSON.parse(data); } catch {}
        try {
            const decrypted = localDecrypt(data);
            if (decrypted) {
                try { return JSON.parse(decrypted); } catch { return decrypted; }
            }
        } catch {}
        try { return JSON.parse(atob(data)); } catch {}
    }
    return null;
};

const getAuthToken = () => {
    const token = tokenStorage.getToken();
    if (!token) {
        throw new Error('Authorization token is missing');
    }
    return `Bearer ${token}`;
};

export const fetchOrganisations = async (userId) => {
    return axiosApi.get(ORGANISATIONS_LIST.replace('{userId}', userId))
        .then(response => response.data.data)
        .catch(error => {
            // Extract serializable error message instead of the whole error object
            const errorMessage = error?.response?.data?.message 
                || error?.message 
                || 'Eroare la încărcarea organizațiilor';
            return {error: errorMessage};
        });
};

export const searchOrganisationByDomain = async (domain) => {
    if (!domain) {
        throw new Error('Domain is required to search organisation by domain');
    }

    try {
        const response = await axiosApi.get(ORGANISATION_SEARCH_BY_DOMAIN, {
            params: {value: domain},
        });

        let data = response.data;
        if (typeof data === 'string') {
            const decrypted = fastDecrypt(data);
            // If decrypted response has a .data property, return that (common pattern)
            // The user specified structure: {"_meta":..., "data": {"organisation":..., "domain_type":...}}
            // So we want to return the inner "data" object.
            data = decrypted?.data || decrypted;
        } else if (data && data.data) {
             // If response is already object and has .data
             data = data.data;
        }

        return data;
    } catch (error) {
        throw new Error(error?.response?.data?.message || error?.message || 'Error searching organisation by domain');
    }
};

export const updateDefaultOrganisation = async (organisationId, userId) => {
    if (!organisationId || !userId) {
        throw new Error('organisationId and userId are required to update default organisation');
    }

    const url = ORGANISATION_UPDATE_DEFAULT
        .replace('{organisationId}', organisationId)
        .replace('{userId}', userId);

    // axiosApi interceptor already adds Authorization header automatically
    const response = await axiosApi.put(url, {});

    return response?.data;
};

export const fetchOrganisationReport = async ({
        organisationID,
        dateFilter,
        startDate,
        endDate,
        owner,
        creator,
        priority,
        status
    }) => {
    const params = {
        organisationID,
        dateFilter,
        ...(dateFilter === 'custom' && {
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
        }),
        owner,
        creator,
        priority,
        status
    };

    try {
        const response = await axiosApi.get(ORGANISATION_REPORT, {
            headers: {
                Authorization: getAuthToken(),
            },
            params
        });

        if (!response.data) {
            throw new Error('Răspunsul din API este gol sau invalid');
        }

        // If response.data is already an object (plain JSON), return it directly
        if (typeof response.data === 'object' && response.data !== null && !Array.isArray(response.data)) {
            return response.data;
        }

        // If response.data is a string, try to parse as JSON first
        if (typeof response.data === 'string') {
            // Check if string is too large for decryption
            if (response.data.length > 10000) {
                try {
                    const parsed = JSON.parse(response.data);
                    return parsed;
                } catch (parseError) {
                }
            } else {
                // Try to parse as JSON first (maybe it's already decrypted)
                try {
                    const parsed = JSON.parse(response.data);
                    return parsed;
                } catch (parseError) {
                }
            }
        }

        const decryptedResponse = fastDecrypt(response.data);

        // Check if decrypt returned the original string wrapped (too large case)
        if (decryptedResponse?.data && typeof decryptedResponse.data === 'string') {
            // If the wrapped string is still very large, try to parse it as JSON
            if (decryptedResponse.data.length > 10000) {
                try {
                    const parsed = JSON.parse(decryptedResponse.data);
                    return parsed;
                } catch (e) {
                    throw new Error('Răspunsul este prea mare pentru decriptare. Vă rugăm să reduceți intervalul de date sau să contactați suportul.');
                }
            }
            // Try to parse the wrapped string as JSON
            try {
                const parsed = JSON.parse(decryptedResponse.data);
                return parsed;
            } catch (e) {
            }
        }

        if (!decryptedResponse || (typeof decryptedResponse === 'object' && Object.keys(decryptedResponse).length === 0)) {
            throw new Error('Răspunsul decriptat este gol sau invalid');
        }

        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

export const fetchOrganisationMembers = async ({organisationID, limit = 20, offset = 0, order = 'permissions', search = ''}) => {
    if (!organisationID) {
        throw new Error('organisationID is required to fetch organisations');
    }

    const userId = tokenStorage.getUserId();
    if (!userId) {
        throw new Error('User ID is required to fetch organisation members');
    }

    const params = {
        limit,
        offset,
        order,
        ...(search && { search })
    };

    try {
        // Backend requires userID in header
        // NOTE: Browser will convert 'userID' to 'userid' (lowercase) in preflight CORS request
        // Backend MUST allow 'userid' in Access-Control-Allow-Headers for this to work
        const response = await axiosApi.get(`${ORGANISATION_MEMBERS}${organisationID}`, {
            headers: {
                Authorization: getAuthToken(),
                'userID': userId, // Required by backend - will be converted to 'userid' by browser
            },
            params
        });

        if (!response.data) {
            throw new Error('Răspunsul din API este gol sau invalid');
        }
        // If response.data is already an object (plain JSON), return it directly
        if (typeof response.data === 'object' && response.data !== null) {
            // Handle both direct object and nested data structure
            return response.data.data || response.data;
        }

        // If response.data is a string, try to decrypt it (like getUserNotifications)
        let decryptedData = response.data;
        if (typeof response.data === 'string') {
            try {
                // First try endpoint decryption
                const decrypted = fastDecrypt(response.data);
                
                // If decrypt returned wrapped string (too large), try local decryption
                if (decrypted?.data && typeof decrypted.data === 'string' && decrypted.data.length > 10000) {
                    // Try local decryption - will use tokenStorage values if available, otherwise static fallback
                    try {
                        const locallyDecrypted = localDecrypt(decrypted.data);
                        if (locallyDecrypted) {
                            // Try to parse as JSON
                            try {
                                decryptedData = JSON.parse(locallyDecrypted);
                            } catch (parseError) {
                                // Use the decrypted string as-is
                                decryptedData = locallyDecrypted;
                            }
                        } else {
                            decryptedData = decrypted?.data || decrypted;
                        }
                    } catch (localDecryptError) {
                        decryptedData = decrypted?.data || decrypted;
                    }
                } else {
                    decryptedData = decrypted?.data || decrypted;
                }
            } catch (decryptError) {
                try {
                    decryptedData = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Nu s-a putut procesa răspunsul de la server. Vă rugăm să reîncercați.');
                }
            }
        }

        // Handle nested data structure - return the full object to let caller extract what they need
        // Response structure: { _meta: {...}, responsibleList: { records: [...], count: ..., offset: ..., total: ... } }
        return decryptedData;
    } catch (error) {
        // Re-throw with user-friendly message if it's our custom error
        if (error.message && (error.message.includes('prea mare') || error.message.includes('Nu s-a putut procesa'))) {
            throw error;
        }
        // Otherwise, wrap in a generic error
        throw new Error(error?.response?.data?.message || error?.message || 'Eroare la încărcarea listei de utilizatori');
    }
};

export const fetchOrganisationTags = async (organisationID) => {
    if (!organisationID) {
        throw new Error('organisationID is required to fetch organisation tags');
    }

    try {
        const response = await axiosApi.get(`${ORGANISATION_TAGS}${organisationID}`, {
            headers: {
                Authorization: getAuthToken(),
            },
        });

        const decryptedResponse = fastDecrypt(response.data);

        if (!decryptedResponse || Object.keys(decryptedResponse).length === 0) {
            throw new Error('Răspunsul decriptat este gol sau invalid');
        }

        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

export const fetchOrganisationsAddForm = async (organisationID) => {
    if (!organisationID) {
        throw new Error('organisationID is required to fetch organisation tags');
    }

    try {
        const response = await axiosApi.get(`${ORGANISATION_ADDFORM}${tokenStorage.getUserId()}?organisationIDs=${organisationID}`, {
            headers: {
                Authorization: getAuthToken()
            },
        });

        const decryptedResponse = fastDecrypt(response.data);

        if (!decryptedResponse || Object.keys(decryptedResponse).length === 0) {
            throw new Error('Răspunsul decriptat este gol sau invalid');
        }

        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

export const createOrganisationPaymentIntent = async ({ user_email, user_id, number_of_users, organisation_id }) => {
    try {
        const response = await axiosApi.post(`${ORGANISATION_SUBSCRIPTION}`, {
            user_email,
            user_id,
            number_of_users,
            organisation_id
        }, {
            headers: {
                Authorization: getAuthToken()
            }
        });

        if (!response.data || !response.data.client_secret) {
            throw new Error('Stripe client_secret missing from response');
        }

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const createOrganisationTag = async ({ organisationID, name }) => {
    if (!organisationID || !name) {
        throw new Error('organisationID și name sunt obligatorii pentru creare tag');
    }

    const userID = tokenStorage.getUserId();
    if (!userID) {
        throw new Error('User ID este obligatoriu pentru creare tag');
    }

    try {
        const response = await axiosApi.post(
            ORGANISATION_ADD_TAG,
            {
                name,
                organisationID,
            },
            {
                headers: {
                    Authorization: getAuthToken(),
                    userID: String(userID),
                },
            }
        );

        const data = response.data;
        if (data?._meta?.status !== 'SUCCESS') {
            throw new Error(data?.message || 'Crearea tag-ului a eșuat');
        }

        return data.tagID;
    } catch (error) {
        throw new Error(
            error?.response?.data?.message ||
            error?.message ||
            'Eroare la crearea tag-ului de organizație'
        );
    }
};

export const exportPontaj = async ({ organisationID, month, userIDs, exportType = 'timesheet' }) => {
    if (!organisationID) throw new Error('organisationID is required');
    if (!month) throw new Error('month is required');

    const userId = tokenStorage.getUserId();
    if (!userId) throw new Error('User ID is required');

    const body = { organisationID, month };
    if (userIDs && userIDs.length > 0) {
        body.userIDs = userIDs;
    }

    // Encrypt body before sending (backend expects encrypted data)
    let encryptedData = await encrypt(body);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data)) {
        if (encryptedData.data.length > 0) {
            payloadToSend = encryptedData.data[0];
        }
    }

    const route = exportType === 'presence' ? ORGANISATION_PRESENCE_EXPORT : ORGANISATION_TIMESHEETS_EXPORT;

    const response = await axiosApi.post(route, payloadToSend, {
        headers: {
            Authorization: getAuthToken(),
            'userID': userId,
            'Content-Type': 'text/plain',
        },
        transformRequest: [(data) => data],
        responseType: 'blob',
    });

    // Trigger browser download
    const filename = exportType === 'presence' ? `presence_${month}.csv` : `timesheet_${month}.csv`;
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};
